import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Menu, X } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BASE_LINKS = [
  // "Home" first, because the site is one long page: without it there is no
  // obvious way back to the top and visitors reach for the browser's back
  // button, which takes them off the site entirely.
  { label: "Home", href: "#top" },
  { label: "Shows", href: "#shows" },
  { label: "Music", href: "#music" },
  { label: "About", href: "#about" },
  { label: "Gallery", href: "#gallery" },
  { label: "Merch", href: "#merch" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const settings = useSiteSettings();

  // Same queryKey/queryFn as <Videos /> — the shared QueryClient dedupes the request.
  const { data: videos = [] } = useQuery({
    queryKey: ["videos"],
    queryFn: async () => (await axios.get(`${API_BASE}/videos`)).data ?? [],
  });

  // Only offer the Videos link when there is actually a section to scroll to.
  const showVideos = settings.videos_visible !== false && videos.length > 0;

  const links = useMemo(() => {
    if (!showVideos) return BASE_LINKS;
    const next = [...BASE_LINKS];
    const at = next.findIndex((l) => l.href === "#gallery");
    next.splice(at < 0 ? next.length : at, 0, { label: "Videos", href: "#videos" });
    return next;
  }, [showVideos]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/80 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="font-anton text-2xl md:text-3xl uppercase tracking-wide text-white"
        >
          <span className="text-[#DC2626]">R.</span>Loveless
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-oswald text-sm uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="nav-book-button"
            className="font-oswald uppercase tracking-[0.18em] text-sm bg-[#B91C1C] hover:bg-[#991B1B] text-white px-5 py-2.5 transition-colors"
          >
            Book the Band
          </a>
        </nav>

        <button
          data-testid="nav-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-black/95 border-t border-white/5 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              className="font-oswald text-base uppercase tracking-[0.18em] text-white/80"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-book-button"
            className="font-oswald uppercase tracking-[0.18em] text-sm bg-[#B91C1C] text-white px-5 py-3 text-center"
          >
            Book the Band
          </a>
        </div>
      )}
    </header>
  );
};
